import {
  APP_ZONES,
  APP_ZONES_TITLE,
  AlertStyleConfigurationType,
  PageNavigationMap,
  Role,
  StyleConfig,
  SystemAllZonesForRole,
  SystemConfiguration,
  SystemSubscriptionPlan,
  SystemSystemConfig,
  SystemZoneDetail,
  UiZoneSubscriptionsConfig,
  WorkflowConfig,
  WorkflowZoneDevicesConfig,
  customDataType,
} from '@finxone-platform/shared/sys-config-types';
import * as objectMapper from 'object-mapper';
import {
  mapZone,
  removeSpaceFromRole,
  zoneMapping,
  zoneMappingForRole,
} from './mapping/utils/zone-mapping.utils';

export function mapWorkflowToSystem(workflowConfig: WorkflowConfig): SystemConfiguration {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: any = objectMapper.merge(workflowConfig, getMappingFromWorkflowToSystemObject());
  return {
    ...result,
    ui: {
      ...result.ui,
    },
  };
}

export function mapWorkflowToRolesArray(workflowConfig: WorkflowConfig): {
  roleKey: string;
  zones: SystemAllZonesForRole;
}[] {
  const result: {
    roleZones: { [roleKey: string]: SystemAllZonesForRole };
  } = objectMapper.merge(workflowConfig, getMappingFromWorkflowToRolesArray()) as any;
  // Map workflowConfig to roleZones
  return Object.entries(result.roleZones).map(([roleKey, zones]) => ({
    roleKey,
    zones,
  })) as {
    roleKey: string;
    zones: SystemAllZonesForRole;
  }[];
}

export function getMappingFromWorkflowToRolesArray(): object {
  return {
    'permissionSections.roles': [
      {
        key: `roleZones`,
        transform: function (roles: Role[]): { [roleKey: string]: SystemAllZonesForRole } {
          const getMappingFunction = function someFunc(
            zoneName: string,
          ): (role: Role) => UiZoneSubscriptionsConfig {
            return (role) => {
              // If zone mapping not found in map then asume zone is mapped as landing zone
              return zoneMappingForRole(zoneName, role, mapZone);
            };
          };
          const roleZones: { [roleKey: string]: SystemAllZonesForRole } = {};

          roles.forEach((role) => {
            roleZones[role.slug] = {};

            Object.entries(role?.zones).forEach(([zoneKey, zone]) => {
              if (zone !== null) {
                roleZones[role.slug][zoneKey] = getMappingFunction(zoneKey)(role);
              }
            });
          });

          return roleZones;
        },
      },
    ],
  };
}

export function getMappingFromWorkflowToSystemObject(): object {
  return {
    // projectId: 'projectId',
    styleConfig: {
      key: 'ui.system',
      transform: function (styleConfig: StyleConfig): SystemSystemConfig {
        return {
          systemName: styleConfig.systemName,
          legalEntityName: styleConfig.legalEntityName,
          theme: {
            fontFamily: styleConfig.fontFamily ?? '',
            secondaryFontFamily: styleConfig.secondaryFontFamily ?? '',
            logo: styleConfig.logo,
          },
          loginPageConfig: styleConfig?.loginPageConfig ?? {
            logoEnabled: false,
          },
          alternativeThemes: Object.fromEntries(
            Object.entries(styleConfig.alternativeThemes ?? {})?.map(([key, theme]) => {
              return [
                key,
                {
                  systemName: theme.systemName,
                  legalEntityName: theme.legalEntityName,
                  theme: {
                    fontFamily: theme.fontFamily ?? '',
                    secondaryFontFamily: theme.secondaryFontFamily ?? '',
                    logo: theme.logo,
                  },
                  loginPageConfig: theme?.loginPageConfig ?? {
                    logoEnabled: false,
                  },
                  logoTitle: theme?.logoTitle ?? 'logo',
                },
              ];
            }),
          ),
        };
      },
    },
    customData: {
      key: 'ui.customData',
      transform: (customData: customDataType) => {
        return customData;
      },
    },
    alertsConfig: {
      key: 'ui.alertsConfig',
      transform: (alertsConfig: AlertStyleConfigurationType) => {
        return alertsConfig;
      },
    },
    pageNavigationMap: {
      key: 'pageNavigationMap',
      transform: function (pageNavigationMap: PageNavigationMap) {
        return pageNavigationMap;
      },
    },
    'permissionSections.subscriptions': {
      key: 'subscription',
      transform: function (subscriptions: SystemSubscriptionPlan) {
        Object.keys(subscriptions).forEach((key) => {
          (subscriptions as any)[key].title = removeSpaceFromRole((subscriptions as any)[key].title);
        });
        return subscriptions;
      },
    },
    'permissionSections.roles': [
      {
        key: `roles`,
        transform: function (roles: Role[]) {
          const rolesMap = {};
          roles.forEach((role) => {
            Object.assign(rolesMap, {
              [role.slug]: {
                id: role.id,
                slug: role.slug,
                title: role.name,
                userType: role.userType,
                alternativeThemes: role.themeOptions,
              },
            });
          });
          return rolesMap;
        },
      },
      {
        key: 'ui.navbar',
        transform: function (roles: Role[]) {
          const roleConfig = {};
          roles.forEach((role) => {
            Object.assign(roleConfig, { [role.slug]: role.navBar });
          });
          return roleConfig;
        },
      },
      {
        key: `ui.zones`,
        transform: function (roles: Role[]) {
          // list of all zones
          const uniqueZones = roles
            .flatMap((role) => Object.keys(role.zones))
            .filter((value, index, array) => array.indexOf(value) === index);
          const getMappingFunction = function fwfwe(zoneName: string): (roles: Role[]) => SystemZoneDetail {
            const map = new Map<string, (roles: Role[]) => SystemZoneDetail>([
              [
                APP_ZONES.LANDING,
                (roles) => {
                  return zoneMapping(APP_ZONES.LANDING, APP_ZONES_TITLE.LANDING, roles, mapZone);
                },
              ],
              [
                APP_ZONES.PAYMENT,
                (roles) => {
                  return zoneMapping(APP_ZONES.PAYMENT, APP_ZONES_TITLE.PAYMENT, roles, mapZone);
                },
              ],
              [
                APP_ZONES.PROFILE,
                (roles) => {
                  return zoneMapping(APP_ZONES.PROFILE, APP_ZONES_TITLE.PROFILE, roles, mapZone);
                },
              ],
              [
                APP_ZONES.VERIFICATION,
                (roles) => {
                  return zoneMapping(APP_ZONES.VERIFICATION, APP_ZONES_TITLE.VERIFICATION, roles, mapZone);
                },
              ],
              [
                APP_ZONES.ONBOARDING,
                (roles) => {
                  return zoneMapping(APP_ZONES.ONBOARDING, APP_ZONES_TITLE.ONBOARDING_PAGE, roles, mapZone);
                },
              ],
              [
                APP_ZONES.GENERAL,
                (roles) => {
                  return zoneMapping(APP_ZONES.GENERAL, APP_ZONES_TITLE.GENERAL, roles, mapZone);
                },
              ],
            ]);

            return map.get(zoneName) !== undefined
              ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                map.get(zoneName)!
              : (roles) => {
                  // If zone mapping not found in map then asume zone is mapped as landing zone
                  return zoneMapping(zoneName, zoneName, roles, mapZone);
                };
          };
          const zones = {};
          uniqueZones.forEach((zoneName) => {
            Object.assign(zones, {
              [zoneName]: getMappingFunction(zoneName)(roles),
            });
          });

          return zones;
        },
      },
      {
        key: `pageUrlToPageIdMap`,
        transform: function (roles: Role[]) {
          const pageUrlToPageIdMap: { [device: string]: { [pageUrl: string]: string } } = {};
          roles.forEach((role) => {
            Object.entries(role.zones).forEach(([, zone]) => {
              if (zone !== null) {
                const config = zone.canvases.subscription?.['default'];
                if (!config) {
                  return;
                }

                const workflowZoneDevicesConfig = config as WorkflowZoneDevicesConfig;
                Object.keys(workflowZoneDevicesConfig).forEach((deviceKey) => {
                  const key = deviceKey as unknown as keyof WorkflowZoneDevicesConfig;
                  const pages = workflowZoneDevicesConfig[key]?.pages;
                  pages?.forEach((page) => {
                    page.pageConfiguration.fullUrls?.forEach((fullUrl) => {
                      if (!pageUrlToPageIdMap[key]) {
                        pageUrlToPageIdMap[key] = {};
                      }
                      const device = pageUrlToPageIdMap[key];
                      if (device !== undefined && fullUrl && page.pageUniqueId !== undefined) {
                        device[fullUrl] = page.pageUniqueId;
                      }
                    });
                  });
                });
              }
            });
          });

          return pageUrlToPageIdMap;
        },
      },
    ],
  };
}
